// Bundles derived TaskItemBase state to keep the controller hook under complexity limits.
import {
  VIEW_FLAGS,
  computeBreadcrumbTooltip,
  canShowAddSubtaskButton,
} from '@/features/tasks/utils/ui/taskListItemUtils';
import { selectionIdsForList } from '@/features/tasks/utils/ui/taskItemStyleUtils';
import { shouldShowRowDelete } from '@/features/tasks/components/context-menu/taskContextMenuConfig';
import { isSchedulableTask } from '@/features/tasks/utils/scheduling/taskSchedulingUtils';
import { getTaskItemMetadata } from './TaskItemBase.render';

export function buildTaskItemContentProps({
  task,
  localCompleted,
  setLocalCompleted,
  finalUser,
  finalHandleToggleComplete,
  finalActiveFocusTaskId,
  finalFocusStartTime,
  finalStopFocus,
  isSmallScreen,
  isExecutionView,
  isTouchDevice,
  breadcrumb,
  isFocused,
  displayDuration,
  showPlayButton,
  finalHandleStartFocus,
  metadataSection,
  onStartAddingSubtask,
  finalHandleDeleteTask,
  view,
  showAddSubtaskButton,
  showRowDelete,
  selectedTaskIds,
  listId,
  focusOwnerId,
  isMultiSelectActive,
  onToggleInSelection,
}) {
  return {
    task,
    localCompleted,
    setLocalCompleted,
    user: finalUser,
    handleToggleComplete: finalHandleToggleComplete,
    activeFocusTaskId: finalActiveFocusTaskId,
    focusStartTime: finalFocusStartTime,
    stopFocus: finalStopFocus,
    isSmallScreen,
    isExecutionView,
    isTouchDevice,
    breadcrumb,
    isFocused,
    displayDuration,
    showPlayButton,
    handleStartFocus: finalHandleStartFocus,
    metadataSection,
    onStartAddingSubtask,
    handleDeleteTask: finalHandleDeleteTask,
    view,
    showAddSubtaskButton,
    showRowDelete,
    selectedTaskIds: selectionIdsForList(selectedTaskIds, listId, focusOwnerId),
    isMultiSelectActive,
    onToggleInSelection,
  };
}

export function buildTaskItemViewFlags(view, menuSurface) {
  return {
    isOrganizeView: VIEW_FLAGS.isOrganizeView(view),
    isMasterView: VIEW_FLAGS.isMasterView(view),
    isPlanView: VIEW_FLAGS.isPlanView(view),
    isDashboardView: VIEW_FLAGS.isDashboardView(view),
    isDashboardSurface: menuSurface === 'dashboard',
    isTodayUpcomingSurface: menuSurface === 'today-upcoming',
    isSubtaskView: VIEW_FLAGS.isSubtaskView(view),
  };
}

export function resolveIsSchedulable(task, tasksMap) {
  return !task?.id || !tasksMap?.size || isSchedulableTask(task, tasksMap);
}

export function buildMergedEventHandlers(
  eventHandlers,
  handleContextMenu,
  task,
) {
  return {
    ...eventHandlers,
    onContextMenu: (event) => handleContextMenu(task, event),
  };
}

export function resolveShowRowDelete(menuSurface) {
  return menuSurface ? shouldShowRowDelete(menuSurface) : false;
}

export function buildBreadcrumbTooltip({
  isExecutionView,
  isPlanView,
  isTouchDevice,
  breadcrumbArray,
}) {
  return computeBreadcrumbTooltip(
    isExecutionView || isPlanView,
    isTouchDevice,
    breadcrumbArray,
  );
}

export function buildTaskItemPresentation({
  style,
  baseClassNames,
  isFirstSubtask,
  isLastSubtask,
  parentContextColor,
}) {
  const classNames = [
    baseClassNames,
    isFirstSubtask && 'is-first-subtask',
    isLastSubtask && 'is-last-subtask',
  ]
    .filter(Boolean)
    .join(' ');

  const finalStyle = {
    ...style,
    '--parent-context-color': parentContextColor,
  };

  return { classNames, finalStyle };
}

export function buildTaskItemMetadataSection({
  isSmallScreen,
  allProjects,
  projectDisplayMap,
  projectColorMap,
  allContexts,
  contextDisplayMap,
  hasConflict,
  onRebalance,
  task,
  formattedDate,
  viewFlags,
  isExecutionView,
  displayDuration,
  isRollup,
  isFocused,
  focusStartTime,
  localCompleted,
  listId = null,
  tasksMap = null,
}) {
  return getTaskItemMetadata({
    isSmallScreen,
    allProjects,
    projectDisplayMap,
    projectColorMap,
    allContexts,
    contextDisplayMap,
    hasConflict,
    onRebalance,
    task,
    formattedDate,
    isOrganizeView: viewFlags.isOrganizeView,
    isMasterView: viewFlags.isMasterView,
    isExecutionView,
    isPlanView: viewFlags.isPlanView,
    isDashboardView: viewFlags.isDashboardView,
    isDashboardSurface: viewFlags.isDashboardSurface,
    isTodayUpcomingSurface: viewFlags.isTodayUpcomingSurface,
    displayDuration,
    isRollup,
    isFocused,
    focusStartTime,
    isSubtaskView: viewFlags.isSubtaskView,
    isCompleted: localCompleted,
    listId,
    tasksMap,
  });
}

export function resolveShowAddSubtaskButton(
  task,
  { view, addSubtaskOnParentsOnly, isSmallScreen, isExecutionView },
) {
  return canShowAddSubtaskButton(task, {
    view,
    addSubtaskOnParentsOnly,
    isSmallScreen,
    isExecutionView,
  });
}

export function createTaskItemDoubleClickHandler(
  navigate,
  location,
  task,
  { disableRowNavigate = false } = {},
) {
  if (disableRowNavigate) return () => {};
  return () => navigate(`${location.pathname}?task=${task.id}`);
}
